(() => {
  const MAX_FILE_BYTES = 15 * 1024 * 1024;
  const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
  const SUPPORTED_PROFILES = new Set([
    'wording',
    'design_brief',
    'wording_and_brief',
    'photo_likeness',
    'photo_likeness_proof',
    'variant_conditional',
  ]);

  // Shopify's Google channel remains the sole owner of standard commerce
  // events. These RN-only signals answer where personalisation or chat causes
  // friction, without sending customer-entered wording, filenames or emails.
  const measure = (name, parameters = {}) => {
    // rn-analytics owns consent, bounded payloads and diagnostic de-duplication.
    // A missing analytics owner is fail-closed rather than a reason to emit a
    // second, unguarded custom-event path from this feature file.
    if (typeof window.rnMeasure !== 'function') return null;
    return window.rnMeasure(name, parameters);
  };

  const pendingAddAttempts = [];
  const guideSelectionSurfaces = new Set([
    '/collections/rat-care-hub',
    '/pages/best-rat-enrichment-toys-uk-what-actually-keeps-rats-busy',
    '/pages/pet-memorial-gift-chooser',
  ]);

  const productMeasurementParameters = (element) => {
    if (typeof window.rnGetProductContext !== 'function') return {};
    const product = window.rnGetProductContext(element) || {};
    const parameters = {};
    if (product.route_id) parameters.rn_route_id = product.route_id;
    if (product.route_path) parameters.rn_route_path = product.route_path;
    if (product.product_id) parameters.rn_product_id = product.product_id;
    if (product.variant_id) parameters.rn_variant_id = product.variant_id;
    if (product.product_handle) parameters.rn_product_handle = product.product_handle;
    if (product.family_id) parameters.rn_family_id = product.family_id;
    if (product.cluster_id) parameters.rn_cluster_id = product.cluster_id;
    if (product.world) parameters.rn_world = product.world;
    return parameters;
  };

  const selectionMeasurementParameters = () => {
    if (typeof window.rnGetSelectionContext !== 'function') return {};
    const selection = window.rnGetSelectionContext() || {};
    return selection.selection_event_id
      ? { rn_selection_event_id: selection.selection_event_id }
      : {};
  };

  const personalisationParameters = (root) => {
    const inputTypes = new Set(
      getInputs(root).map((input) => {
        if (input.type === 'file') return 'photo';
        if (input.type === 'email') return 'proof_email';
        return root.dataset.rnProfile === 'design_brief' ? 'design_brief' : 'wording';
      })
    );
    return {
      rn_profile: root.dataset.rnProfile || 'unknown',
      rn_input_type: Array.from(inputTypes).sort().join(','),
      ...productMeasurementParameters(root),
      ...selectionMeasurementParameters(),
    };
  };

  const getField = (input) => input.closest('[data-rn-field]');
  const getInputs = (root) => Array.from(
    root.querySelectorAll('[data-rn-personalisation-input], [data-rn-photo]')
  );
  const isActive = (root) =>
    root.dataset.rnConditionalActive !== 'false' && !root.hidden && !root.disabled;

  const formRoot = (form) => Array.from(document.querySelectorAll('[data-rn-personalisation]'))
    .find((root) => root.dataset.rnFormId === form?.id && isActive(root));
  const formFieldName = (input) => {
    if (input.matches('[data-rn-photos-later-choice]')) return 'photos_later';
    if (input.type === 'file') return `photo_${input.dataset.rnPhotoIndex}`;
    return getField(input)?.dataset.rnAnalyticsField;
  };
  const measureFormEvent = (name, root, input) => measure(name, {
    form_id: 'rn_personalisation',
    rn_product_handle: root.dataset.rnProductHandle,
    rn_profile: root.dataset.rnProfile,
    ...(input ? { field_name: formFieldName(input) } : {}),
  });
  const formAttemptsMeasured = new WeakSet();
  const measureFormAttempt = (root) => {
    if (!root || formAttemptsMeasured.has(root)) return;
    if (!measureFormEvent('add_to_cart_attempt', root)) return;
    formAttemptsMeasured.add(root);
    window.setTimeout(() => formAttemptsMeasured.delete(root), 0);
  };
  const fieldErrorsMeasured = new WeakSet();
  const measureFormError = (root, input) => {
    if (fieldErrorsMeasured.has(input)) return;
    if (!measureFormEvent('validation_error', root, input)) return;
    fieldErrorsMeasured.add(input);
    window.setTimeout(() => fieldErrorsMeasured.delete(input), 0);
  };

  const syncPhotosLater = (root) => {
    const choice = root.querySelector('[data-rn-photos-later-choice]');
    if (!choice) return;
    const deferred = choice.checked && isActive(root);
    const uploadGroup = root.querySelector('[data-rn-photo-upload-group]');
    const helper = root.querySelector('[data-rn-photos-later-help]');
    const handoff = root.querySelector('[data-rn-photos-later-handoff]');
    const property = root.querySelector('[data-rn-photos-later-property]');
    const saveMessage = root.querySelector('[data-rn-photo-save-message]');
    if (uploadGroup) uploadGroup.hidden = deferred;
    if (helper) helper.hidden = !deferred;
    if (handoff) handoff.hidden = !deferred;
    if (property) property.disabled = !deferred;
    if (saveMessage) saveMessage.hidden = deferred;
    getInputs(root).filter((input) => input.type === 'file').forEach((input) => {
      input.disabled = deferred || !isActive(root);
      input.required = !deferred && input.hasAttribute('data-rn-required');
      if (deferred) setError(input);
    });
    const photoFlag = root.querySelector('[data-rn-photo-flag]');
    if (photoFlag) photoFlag.disabled = deferred || !getInputs(root)
      .filter((input) => input.type === 'file')
      .some((input) => !input.disabled && input.files.length > 0);
  };

  // Native form validation fires `invalid` on each failing control and then
  // suppresses `submit` entirely. Keep one short-lived marker per
  // personalisation root so that one attempted add creates one diagnostic,
  // whether the browser takes the native path or our submit handler does.
  const validationDiagnosticsMeasured = new WeakSet();

  const validationFieldType = (root, input) => input.type === 'file'
    ? 'photo'
    : input.type === 'email'
      ? 'proof_email'
      : root.dataset.rnProfile === 'design_brief'
        ? 'design_brief'
        : 'wording';

  const measureValidationError = (root, input) => {
    if (root && input) measureFormError(root, input);
    if (!root || !input || validationDiagnosticsMeasured.has(root)) return null;
    const invalidFields = Array.from(new Set(
      getInputs(root)
        .filter((candidate) => !candidate.validity.valid)
        .map((candidate) => validationFieldType(root, candidate))
    )).sort();
    const eventId = measure('rn_personalisation_validation_error', {
      ...personalisationParameters(root),
      rn_field_type: validationFieldType(root, input),
      rn_first_invalid_field: validationFieldType(root, input),
      rn_invalid_fields: invalidFields.join(','),
    });
    if (!eventId) return null;
    validationDiagnosticsMeasured.add(root);
    // All native invalid events for one interactive validation pass are
    // delivered in the same task. Release the marker before a later user
    // attempt, while still suppressing duplicate controls in this pass.
    window.setTimeout(() => validationDiagnosticsMeasured.delete(root), 0);
    return eventId;
  };

  const personalisationRootForInvalidInput = (input) => {
    const root = input?.closest?.('[data-rn-personalisation]');
    if (!root || !isActive(root) || !getInputs(root).includes(input)) return null;
    const form = document.getElementById(root.dataset.rnFormId)
      || input.form
      || input.closest?.('form');
    if (!form?.matches?.('form[data-type="add-to-cart-form"]')) return null;
    if (!form.querySelector?.('[name="id"]')) return null;
    return root;
  };

  const handleNativeInvalid = (event) => {
    const input = event.target;
    const root = personalisationRootForInvalidInput(input);
    if (!root) return;
    measureFormAttempt(root);
    measureValidationError(root, input);
  };

  const setError = (input, message = '') => {
    const field = getField(input);
    if (!field) return;
    field.classList.toggle('rn-field--error', Boolean(message));
    input.setCustomValidity(message);
    input.setAttribute('aria-invalid', message ? 'true' : 'false');
    const output = field.querySelector('[data-rn-field-error]');
    if (output) output.textContent = message;
  };

  const requiredMessage = (input) => {
    if (input.type === 'file') return 'Choose at least one photo before adding this item.';
    if (input.type === 'email') return 'Enter the email address for this order.';
    const label = input.dataset.rnFieldLabel || 'this required detail';
    return `Add ${label.toLowerCase()} before adding this item.`;
  };

  const validateInput = (input) => {
    if (input.disabled) {
      setError(input);
      return true;
    }

    // Clear our previous custom message before checking the browser's native
    // constraints. This keeps native required, type and maxlength rules in
    // force without making a previous custom error sticky.
    input.setCustomValidity('');

    if (input.type === 'file') {
      if (input.required && input.files.length === 0) {
        setError(input, requiredMessage(input));
        return false;
      }

      const invalidFile = Array.from(input.files).find((file) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        return file.size > MAX_FILE_BYTES || !ALLOWED_EXTENSIONS.includes(extension);
      });

      if (invalidFile) {
        const extension = invalidFile.name.split('.').pop()?.toLowerCase();
        const message = invalidFile.size > MAX_FILE_BYTES
          ? `${invalidFile.name} is larger than 15 MB. Choose a smaller photo.`
          : `${extension?.toUpperCase() || 'This file'} is not supported. Choose JPG, PNG, WebP, HEIC or HEIF.`;
        setError(input, message);
        return false;
      }

      setError(input);
      return true;
    }

    if (input.required && !input.value.trim()) {
      setError(input, requiredMessage(input));
      return false;
    }

    if (input.type === 'email' && input.value.trim() && input.validity.typeMismatch) {
      setError(input, 'Enter a valid email address for this order.');
      return false;
    }

    if (!input.validity.valid) {
      setError(input, input.validationMessage || 'Check this detail before adding the item.');
      return false;
    }

    setError(input);
    return true;
  };

  const updateCounter = (input) => {
    if (!input.id || input.maxLength < 0) return;
    const counter = document.querySelector(`[data-rn-count-for="${CSS.escape(input.id)}"]`);
    if (counter) counter.textContent = `${input.value.length}/${input.maxLength}`;
  };

  const updatePhotoLabel = (input) => {
    const upload = input.closest('.rn-upload');
    if (!upload) return;

    const status = upload.querySelector('[data-rn-upload-status]');
    const preview = upload.querySelector('[data-rn-upload-preview]');
    const image = upload.querySelector('[data-rn-upload-image]');
    const name = upload.querySelector('[data-rn-upload-name]');
    const file = input.files[0];

    if (input.dataset.rnPreviewUrl) {
      URL.revokeObjectURL(input.dataset.rnPreviewUrl);
      delete input.dataset.rnPreviewUrl;
    }

    if (!file) {
      if (status) status.textContent = '';
      if (name) {
        name.textContent = '';
        name.removeAttribute('title');
        name.removeAttribute('aria-label');
      }
      if (preview) preview.hidden = true;
      if (image) {
        image.removeAttribute('src');
        image.alt = '';
        image.hidden = true;
      }
      upload.classList.remove('rn-upload--ready', 'rn-upload--no-preview');
      return;
    }

    if (status) status.textContent = 'Photo selected';
    if (name) {
      name.textContent = 'Ready for your basket';
      name.title = file.name;
      name.setAttribute('aria-label', `Selected file: ${file.name}`);
    }
    if (preview) preview.hidden = false;
    upload.classList.add('rn-upload--ready');

    if (!image) return;
    const previewUrl = URL.createObjectURL(file);
    input.dataset.rnPreviewUrl = previewUrl;
    image.src = previewUrl;
    image.alt = `Preview of ${file.name}`;
    image.hidden = false;
    image.onload = () => upload.classList.remove('rn-upload--no-preview');
    image.onerror = () => {
      upload.classList.add('rn-upload--no-preview');
      image.alt = '';
      image.hidden = true;
    };
  };

  const getButtons = (formId) => {
    const form = document.getElementById(formId);
    if (!form) return [];
    return [
      ...form.querySelectorAll('button[type="submit"][name="add"]'),
      ...document.querySelectorAll(`button[type="submit"][name="add"][form="${CSS.escape(formId)}"]`)
    ];
  };

  const formMeasurementParameters = (form) => ({
    ...productMeasurementParameters(form),
    ...selectionMeasurementParameters(),
  });

  const removePendingAddAttempt = (pending) => {
    const index = pendingAddAttempts.indexOf(pending);
    if (index !== -1) pendingAddAttempts.splice(index, 1);
    pending?.form?.removeAttribute('data-rn-measurement-add-pending');
  };

  const rememberAddAttempt = (form) => {
    if (!form || form.dataset.rnMeasurementAddPending === 'true') return;
    const attemptEventId = measure('rn_funnel_add_to_cart_attempt', formMeasurementParameters(form));
    if (!attemptEventId && !formAttemptsMeasured.has(formRoot(form))) return;

    const parameters = formMeasurementParameters(form);
    const pending = {
      form,
      attemptEventId,
      variantId: parameters.rn_variant_id || null,
      parameters,
    };
    form.dataset.rnMeasurementAddPending = 'true';
    pendingAddAttempts.push(pending);
    window.setTimeout(() => removePendingAddAttempt(pending), 15000);
  };

  const forgetOldestAddAttempt = () => {
    const pending = pendingAddAttempts[0];
    if (pending) removePendingAddAttempt(pending);
  };

  const takeSuccessfulAddAttempt = (product) => {
    const items = Array.isArray(product?.items) ? product.items : [product];
    const variantIds = new Set(items
      .map((item) => String(item?.variant_id || ''))
      .filter((id) => /^\d{1,30}$/.test(id))
      .map((id) => `gid://shopify/ProductVariant/${id}`));
    const index = pendingAddAttempts.findIndex((pending) => variantIds.has(pending.variantId));
    if (index === -1) return null;
    const pending = pendingAddAttempts.splice(index, 1)[0];
    if (!pending) return null;
    pending.form?.removeAttribute('data-rn-measurement-add-pending');
    return pending;
  };

  const normaliseProductId = (value) => {
    const id = String(value || '').trim();
    return /^\d{1,30}$/.test(id) ? `gid://shopify/Product/${id}` : id;
  };

  const handleGuideProductSelection = (event) => {
    const anchor = event.target?.closest?.('a[href]');
    if (!anchor) return;
    const currentPath = String(window.location?.pathname || '').split(/[?#]/, 1)[0];
    if (!guideSelectionSurfaces.has(currentPath)) return;

    let destination;
    try {
      destination = new URL(anchor.getAttribute('href'), window.location.origin);
    } catch (_error) {
      return;
    }
    if (destination.origin !== window.location.origin || !/^\/products\//i.test(destination.pathname)) return;

    const productId = anchor.dataset.productId
      || anchor.closest('[data-product-id]')?.dataset?.productId
      || '';
    const productHandle = anchor.dataset.productHandle
      || anchor.closest('[data-product-handle]')?.dataset?.productHandle
      || destination.pathname.split('/')[2]
      || '';
    const parameters = {
      rn_route_id: `route:${currentPath}`,
      rn_route_path: currentPath,
      rn_product_handle: productHandle,
    };
    if (productId) parameters.rn_product_id = normaliseProductId(productId);
    const familyId = anchor.dataset.rnFamilyId
      || anchor.closest('[data-rn-family-id]')?.dataset?.rnFamilyId
      || '';
    if (familyId) parameters.rn_family_id = familyId;
    if (typeof window.rnRecordGuideProductClick === 'function') {
      window.rnRecordGuideProductClick(parameters);
    } else {
      // Fail closed when the bounded owner has not been loaded yet.
      measure('rn_guide_product_click', parameters);
    }
  };

  const initFunnelMeasurement = () => {
    if (window.__rnHyperFunnelMeasurementReady === true) return;
    window.__rnHyperFunnelMeasurementReady = true;

    // This bubbling listener runs after the native product-form submit handler.
    // The personalisation validator stops propagation for invalid forms, so a
    // rejected form cannot create an attempt or later success trace.
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!form?.matches?.('form[data-type="add-to-cart-form"]')) return;
      if (!form.querySelector('[name="id"]')) return;
      rememberAddAttempt(form);
    });
    document.addEventListener('click', (event) => {
      const button = event.target?.closest?.('button[type="submit"], input[type="submit"]');
      if (button?.form?.matches?.('form[data-type="add-to-cart-form"]')) measureFormAttempt(formRoot(button.form));
    }, true);
    document.addEventListener('submit', (event) => {
      if (event.target?.matches?.('form[data-type="add-to-cart-form"]')) measureFormAttempt(formRoot(event.target));
    }, true);
    // `invalid` does not bubble, so capture it at document scope. This is the
    // only path that observes browser-native required/type validation, where
    // no submit event reaches the custom form handler.
    document.addEventListener('invalid', handleNativeInvalid, true);
    document.addEventListener('product-ajax:error', forgetOldestAddAttempt);
    document.addEventListener('click', handleGuideProductSelection, true);
  };

  const setButtonCopy = (root, complete) => {
    const formId = root.dataset.rnFormId;
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;

    const personalisationActive = isActive(root);
    const mainButton = form.querySelector('button[type="submit"][name="add"] .btn__text');
    if (mainButton) {
      mainButton.textContent = personalisationActive
        ? (root.dataset.rnCtaLabel || 'Add personalised item to basket')
        : 'Add to basket';
    }

    document.querySelectorAll(`button[type="submit"][name="add"][form="${CSS.escape(formId)}"] .btn__text`)
      .forEach((label) => {
        label.textContent = personalisationActive && !complete
          ? 'Add details'
          : 'Add to basket';
      });
  };

  const blockUnconfiguredPurchase = (root) => {
    const hasKnownProfile = SUPPORTED_PROFILES.has(root.dataset.rnProfile || '');
    const hasValidSchema = root.dataset.rnSchemaValid === 'true';
    if (root.dataset.rnConfigurationError !== 'true' && hasKnownProfile && hasValidSchema) return false;

    root.dataset.rnConfigurationError = 'true';
    if (root.matches('fieldset')) {
      root.hidden = true;
      root.disabled = true;
      root.setAttribute('aria-hidden', 'true');
    }
    getButtons(root.dataset.rnFormId).forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      const label = button.querySelector('.btn__text');
      if (label) label.textContent = 'Personalisation unavailable';
    });
    return true;
  };

  const parseTriggerValues = (root) => {
    const raw = root.dataset.rnTriggerValues || '';
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return raw.split('||').map((value) => value.trim()).filter(Boolean);
    }
  };

  const selectedTriggerValue = (root, variant) => {
    if (!variant) return '';
    const index = Number.parseInt(root.dataset.rnTriggerOptionIndex || '', 10);
    if (!Number.isInteger(index) || index < 0) return '';
    const value = variant.options?.[index] ?? variant[`option${index + 1}`];
    return value ? String(value).trim().toLowerCase() : '';
  };

  const setConditionalState = (root, variant) => {
    if (root.dataset.rnProfile !== 'variant_conditional') return;
    const triggers = parseTriggerValues(root).map((value) => String(value).trim().toLowerCase());
    const selectedValue = selectedTriggerValue(root, variant);
    const active = Boolean(variant) && triggers.length > 0 && triggers.includes(selectedValue);

    root.dataset.rnConditionalActive = active ? 'true' : 'false';
    root.hidden = !active;
    root.classList.toggle('hidden', !active);
    root.disabled = !active;
    root.setAttribute('aria-hidden', active ? 'false' : 'true');

    getInputs(root).forEach((input) => {
      input.disabled = !active;
      if (!active) setError(input);
    });
    syncPhotosLater(root);

    const complete = !active || getInputs(root)
      .filter((input) => input.required && !input.disabled)
      .every((input) => input.type === 'file' ? input.files.length > 0 : Boolean(input.value.trim()));
    setButtonCopy(root, complete);
  };

  const initialVariantFromRoot = (root) => {
    if (root.dataset.rnProfile !== 'variant_conditional') return null;
    const index = Number.parseInt(root.dataset.rnTriggerOptionIndex || '', 10);
    const value = (root.dataset.rnInitialTriggerValue || '').trim();
    if (!Number.isInteger(index) || index < 0 || !value) return null;
    const options = [];
    options[index] = value;
    return { options };
  };

  const initPersonalisation = (root) => {
    if (blockUnconfiguredPurchase(root)) return;

    const form = document.getElementById(root.dataset.rnFormId);
    if (!form) return;
    if (root.dataset.rnReady !== 'true' && root.dataset.rnProfile === 'variant_conditional') {
      setConditionalState(root, initialVariantFromRoot(root));
    }
    syncPhotosLater(root);

    if (root.dataset.rnReady === 'true') {
      const required = getInputs(root).filter((input) => input.required && !input.disabled);
      const complete = required.every((input) => {
        if (input.type === 'file') return input.files.length > 0;
        return Boolean(input.value.trim()) && input.validity.valid;
      });
      setButtonCopy(root, complete);
      return;
    }
    root.dataset.rnReady = 'true';

    const completedFields = new Set();
    const startForm = () => {
      if (!isActive(root) || root.dataset.rnFormStarted === 'true') return;
      if (measureFormEvent('form_start', root)) root.dataset.rnFormStarted = 'true';
    };
    root.addEventListener('focusin', startForm);
    root.addEventListener('pointerdown', startForm);
    root.addEventListener('input', startForm);
    root.addEventListener('change', (event) => {
      startForm();
      const input = event.target;
      if (!input.matches('[data-rn-photo], [data-rn-photos-later-choice]')) return;
      completeField(input);
    });
    const completeField = (input) => {
      if (!isActive(root) || input.disabled) return;
      const key = formFieldName(input);
      if (!key) return;
      const hasValue = input.type === 'checkbox' ? input.checked
        : input.type === 'file' ? input.files.length > 0 : Boolean(input.value.trim());
      if (input.type === 'file' && !hasValue) {
        input.setCustomValidity('');
        return;
      }
      // Leaving an empty field is not an error yet; the add attempt reports it.
      if (input.type !== 'checkbox' && !hasValue) return;
      if (input.type !== 'checkbox' && !validateInput(input)) {
        measureFormError(root, input);
        return;
      }
      if (!hasValue || completedFields.has(key)) return;
      if (measureFormEvent('field_completed', root, input)) completedFields.add(key);
    };
    root.addEventListener('focusout', (event) => {
      if (event.target.matches('[data-rn-personalisation-input]')) completeField(event.target);
    });

    const inputs = getInputs(root);
    const isComplete = () => {
      if (!isActive(root)) return true;
      return inputs
        .filter((input) => input.required && !input.disabled)
        .every((input) => {
          if (input.type === 'file') return input.files.length > 0 && validateInput(input);
          return Boolean(input.value.trim()) && input.validity.valid;
        });
    };

    const photosLaterChoice = root.querySelector('[data-rn-photos-later-choice]');
    if (photosLaterChoice) photosLaterChoice.addEventListener('change', () => {
      syncPhotosLater(root);
      setButtonCopy(root, isComplete());
    });

    inputs.forEach((input) => {
      const eventName = input.type === 'file' ? 'change' : 'input';
      input.addEventListener(eventName, () => {
        validateInput(input);
        updateCounter(input);
        if (root.dataset.rnMeasuredStarted !== 'true') {
          const startedEventId = measure('rn_personalisation_start', personalisationParameters(root));
          if (startedEventId) root.dataset.rnMeasuredStarted = 'true';
        }
        if (input.type === 'file') {
          updatePhotoLabel(input);
          if (input.files.length > 0 && input.validity.valid && root.dataset.rnMeasuredPhoto !== 'true') {
            const photoEventId = measure('rn_photo_selected', personalisationParameters(root));
            if (photoEventId) root.dataset.rnMeasuredPhoto = 'true';
          }
        }
        const complete = isComplete();
        setButtonCopy(root, complete);
        if (complete && root.dataset.rnMeasuredComplete !== 'true') {
          const completeEventId = measure('rn_personalisation_completed', personalisationParameters(root));
          if (completeEventId) root.dataset.rnMeasuredComplete = 'true';
        }
      });
      input.addEventListener('invalid', () => validateInput(input));
      updateCounter(input);
      if (input.type === 'file') updatePhotoLabel(input);
    });

    form.addEventListener('submit', (event) => {
      if (!isActive(root)) return;
      const firstInvalid = getInputs(root)
        .filter((input) => !input.disabled)
        .find((input) => !validateInput(input));
      if (!firstInvalid) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      form.dispatchEvent(
        new CustomEvent('rn:personalisation-invalid', {
          bubbles: true,
          detail: { input: firstInvalid },
        })
      );
      measureValidationError(root, firstInvalid);
      firstInvalid.focus({ preventScroll: true });
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      firstInvalid.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    }, true);

    setButtonCopy(root, isComplete());
  };

  const initAll = () => document.querySelectorAll('[data-rn-personalisation]').forEach(initPersonalisation);

  const initChatMeasurement = () => {
    const chat = document.querySelector('shopify-chat');
    if (!chat || chat.dataset.rnMeasurementReady === 'true') return;
    chat.dataset.rnMeasurementReady = 'true';
    chat.addEventListener('click', () => {
      if (chat.dataset.rnMeasuredOpen === 'true') return;
      chat.dataset.rnMeasuredOpen = 'true';
      measure('rn_chat_opened', { rn_page_path: window.location.pathname });
    }, { capture: true });
  };

  const initMeasurement = () => {
    initAll();
    initFunnelMeasurement();
    initChatMeasurement();
    if (!document.querySelector('shopify-chat')) {
      const observer = new MutationObserver(() => {
        initChatMeasurement();
        if (document.querySelector('shopify-chat')) observer.disconnect();
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 15000);
    }
  };

  document.addEventListener('DOMContentLoaded', initMeasurement);
  document.addEventListener('shopify:section:load', initMeasurement);
  document.addEventListener('product-ajax:added', (event) => {
    const pending = takeSuccessfulAddAttempt(event.detail?.product);
    if (pending) {
      const root = formRoot(pending.form);
      if (root) measureFormEvent('add_to_cart_success', root);
      measure('rn_add_to_cart_success', pending.parameters);
      // Cart attribution follows the confirmed native/cart owner response.
      // The helper is deduplicated, consent-gated and safe for ordinary and
      // private baskets; the private portal handoff remains separate below.
      if (typeof window.rnPersistCartAttribution === 'function') {
        Promise.resolve(window.rnPersistCartAttribution()).catch(() => {});
      }
    }
  });
  document.addEventListener('variant:changed', (event) => {
    document.querySelectorAll('[data-rn-personalisation]').forEach((root) => {
      setConditionalState(root, event.detail?.variant);
    });
    requestAnimationFrame(initAll);
  });
})();

// Retired portal checkout code is deliberately inert. Photo orders now use
// Shopify's required pre-checkout file properties and native checkout only.
(() => {
  if (window.__rnPortalCheckoutGuard) return;
  window.__rnPortalCheckoutGuard = true;
  return;

  let navigating = false;
  let activeTrigger = null;

  const portalBasketPresent = () => Boolean(document.querySelector('[data-rn-portal-checkout-token]'));

  const handoffFromCart = (cart) => {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    for (const item of items) {
      const token = item?.properties?.['_Portal handoff'];
      if (/^[A-Za-z0-9_-]{20,100}$/.test(String(token || ''))) return String(token);
    }
    return '';
  };

  const setBusy = (busy) => {
    if (!activeTrigger) return;
    activeTrigger.toggleAttribute('aria-busy', busy);
    if ('disabled' in activeTrigger) activeTrigger.disabled = busy;
    const label = activeTrigger.querySelector?.('.btn__text');
    if (!label) return;
    if (busy) {
      label.dataset.rnCheckoutLabel = label.textContent;
      label.textContent = 'Preparing secure checkout…';
    } else if (label.dataset.rnCheckoutLabel) {
      label.textContent = label.dataset.rnCheckoutLabel;
      delete label.dataset.rnCheckoutLabel;
    }
  };

  const saveHandoff = async (token) => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({ attributes: { _rn_portal_handoff: token } }),
        });
        if (!response.ok) throw new Error('Could not save private-order handoff');
        const cart = await response.json();
        if (cart?.attributes?._rn_portal_handoff === token) return true;
      } catch (error) {
        if (attempt === 1) return false;
      }
    }
    return false;
  };

  const ensureHandoff = async () => {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Could not read basket');
      const cart = await response.json();
      const token = handoffFromCart(cart);
      if (!token) return false;
      if (cart.attributes?._rn_portal_handoff === token) return true;
      return saveHandoff(token);
    } catch (error) {
      return false;
    }
  };

  const prepareCheckout = async (event, trigger) => {
    if (!portalBasketPresent()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (navigating) return;
    navigating = true;
    activeTrigger = trigger || null;
    setBusy(true);

    const attribution = typeof window.rnPersistCartAttribution === 'function'
      ? Promise.resolve(window.rnPersistCartAttribution()).catch(() => true)
      : Promise.resolve(true);
    const [, ready] = await Promise.all([attribution, ensureHandoff()]);
    if (ready) {
      window.location.assign('/checkout');
      return;
    }

    navigating = false;
    setBusy(false);
    activeTrigger = null;
    window.alert("We couldn't prepare your private order page. Your basket is safe. Please try checkout again.");
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target?.closest?.(
      'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href$="/cart/checkout"]'
    );
    if (trigger) prepareCheckout(event, trigger);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form?.querySelector) return;
    const trigger = event.submitter;
    if (trigger?.getAttribute?.('name') === 'checkout' || form.querySelector('[name="checkout"]')) {
      prepareCheckout(event, trigger);
    }
  }, true);
})();
